/**
 * Appointment statuses as the Schedule shows them. Stored values are lowercase strings
 * (Appointment.status). `scheduled` is the legacy default every client still writes, so it IS
 * "Confirmed"; a booking not yet confirmed with the owner is `tentative`.
 */
export type AppointmentStatus = 'tentative' | 'scheduled' | 'checked_in' | 'completed' | 'no_show' | 'cancelled';

export interface StatusStyle {
  value: AppointmentStatus;
  label: string;
  /** Dot and selected edge. */
  dot: string;
  /** Selected fill and text. */
  fill: string;
  ink: string;
}

export const STATUSES: StatusStyle[] = [
  { value: 'tentative', label: 'Tentative', dot: '#C9772B', fill: '#FCF0E3', ink: '#7A4314' },
  { value: 'scheduled', label: 'Confirmed', dot: '#2F8F57', fill: '#EAF5EE', ink: '#1D5C38' },
  { value: 'checked_in', label: 'Checked in', dot: '#3B6FC4', fill: '#E8F0FB', ink: '#1E4785' },
  { value: 'completed', label: 'Completed', dot: '#6B7280', fill: '#F2F3F5', ink: '#374151' },
  { value: 'no_show', label: 'No-show', dot: '#D0452F', fill: '#FDEBE8', ink: '#8A1F13' },
  { value: 'cancelled', label: 'Cancelled', dot: '#9CA3AF', fill: '#F3F4F6', ink: '#4B5563' },
];

/**
 * Other spellings in the data read as the canonical value. iOS ships "In Progress" (`in_progress`
 * over /sync): it shows as Checked in, and the editor never writes a status the person did not change.
 */
const ALIASES: Record<string, AppointmentStatus> = {
  confirmed: 'scheduled',
  'no-show': 'no_show',
  'checked-in': 'checked_in',
  in_progress: 'checked_in',
  'in-progress': 'checked_in',
  canceled: 'cancelled',
};

export const normalizeStatus = (status?: string | null): AppointmentStatus => {
  const key = (status ?? '').trim().toLowerCase();
  if (STATUSES.some((s) => s.value === key)) return key as AppointmentStatus;
  return ALIASES[key] ?? 'scheduled';
};

export const statusStyle = (status?: string | null): StatusStyle =>
  STATUSES.find((s) => s.value === normalizeStatus(status))!;

export const isCancelled = (status?: string | null) => normalizeStatus(status) === 'cancelled';
